if (!customElements.get('cart-drawer-crosssell')) {
  class CartDrawerCrosssell extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('[data-crosssell-track]');
      this.controls = this.querySelector('[data-crosssell-controls]');
      this.prevButton = this.querySelector('[data-crosssell-prev]');
      this.nextButton = this.querySelector('[data-crosssell-next]');
      this.progressValue = this.querySelector('[data-crosssell-progress]');
      this.currentLabel = this.querySelector('[data-crosssell-current]');
      this.totalLabel = this.querySelector('[data-crosssell-total]');
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

      this.onAddClick = this.onAddClick.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onPrevClick = () => this.scrollByCards(-1);
      this.onNextClick = () => this.scrollByCards(1);
    }

    connectedCallback() {
      this.addEventListener('click', this.onAddClick);
      this.addEventListener('change', this.onVariantChange);
      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrevClick);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNextClick);
      if (this.track) this.track.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.dataset.mode === 'auto') {
        this.loadRecommendations();
      } else {
        this.initCarousel();
      }
    }

    async loadRecommendations() {
      const url = this.dataset.recommendationsUrl;
      const limit = parseInt(this.dataset.limit, 10) || 8;
      const excludeIds = (this.dataset.cartProductIds || '')
        .split(',')
        .filter(Boolean)
        .map((id) => String(id));
      const sourceIds = excludeIds.slice(0, 3);
      const blockedIds = (this.dataset.excludeProductIds || '')
        .split(',')
        .filter(Boolean)
        .map((id) => String(id));

      if (!url || sourceIds.length === 0) {
        this.useFallbackOrRemove();
        return;
      }

      // Shopify caps recommendations at 10 per request; always ask for the
      // max so blocked or in-cart products can be replaced from the spares.
      const fetchLimit = 10;
      const seen = new Set([...excludeIds, ...blockedIds]);
      const products = [];

      const fetchForIntent = (intent) =>
        Promise.all(
          sourceIds.map((productId) =>
            fetch(`${url}?product_id=${productId}&limit=${fetchLimit}&intent=${intent}`, { credentials: 'same-origin' })
              .then((res) => (res.ok ? res.json() : { products: [] }))
              .catch(() => ({ products: [] }))
          )
        );

      const collect = (responses) => {
        responses.forEach((data) => {
          (data.products || []).forEach((product) => {
            const id = String(product.id);
            if (seen.has(id) || products.length >= limit) return;
            seen.add(id);
            products.push(product);
          });
        });
      };

      try {
        // Fetch both intents at once, then fill slots in order:
        // complementary, then related, then the static list.
        const [complementary, related] = await Promise.all([
          fetchForIntent('complementary'),
          fetchForIntent('related'),
        ]);
        collect(complementary);
        collect(related);

        if (products.length === 0) {
          this.useFallbackOrRemove();
          return;
        }

        this.track.innerHTML = products.map((product) => this.renderCard(product)).join('');
        this.appendFallbackCards(seen, limit - products.length);
        this.initCarousel();
      } catch (error) {
        this.useFallbackOrRemove();
      }
    }

    appendFallbackCards(seen, slots) {
      const fallback = this.querySelector('[data-crosssell-fallback]');
      if (slots <= 0 || !fallback || !fallback.content) return;
      Array.from(fallback.content.querySelectorAll('[data-crosssell-card]')).forEach((card) => {
        const id = String(card.dataset.productId);
        if (slots <= 0 || seen.has(id)) return;
        seen.add(id);
        this.track.appendChild(card.cloneNode(true));
        slots -= 1;
      });
    }

    useFallbackOrRemove() {
      const fallback = this.querySelector('[data-crosssell-fallback]');
      if (fallback && fallback.content && fallback.content.textContent.trim()) {
        this.track.innerHTML = '';
        this.track.appendChild(fallback.content.cloneNode(true));
        this.initCarousel();
        return;
      }
      this.remove();
    }

    renderCard(product) {
      const variants = product.variants || [];
      const hasOnlyDefaultVariant = variants.length <= 1;
      const firstVariant = variants[0] || {};
      const variantImage = firstVariant.featured_image || firstVariant.image || null;
      const image = variantImage || product.featured_image || (product.images && product.images[0]) || null;
      const imageUrl = image ? this.resizeImage(typeof image === 'string' ? image : image.src, 300) : '';
      const initialPrice = firstVariant.price != null ? firstVariant.price : product.price;
      const initialComparePrice = firstVariant.compare_at_price;
      const onSale = initialComparePrice > initialPrice;

      const badge = onSale
        ? `<span class="crosssell-card__badge crosssell-card__badge--sale">Sale</span>`
        : '';

      const price = `<s class="crosssell-card__price-compare" data-crosssell-price-compare${
        onSale ? '' : ' hidden'
      }>${this.formatMoney(initialComparePrice)}</s><span class="crosssell-card__price-final" data-crosssell-price-final>${this.formatMoney(
        initialPrice
      )}</span>`;

      let formFields;
      if (hasOnlyDefaultVariant) {
        formFields = `
          <div class="crosssell-card__select-spacer" aria-hidden="true"></div>
          <input type="hidden" data-crosssell-variant-id value="${firstVariant.id || ''}">
          <button type="button" class="crosssell-card__submit button" data-crosssell-submit>+ Add</button>
        `;
      } else {
        const optionName = this.getOptionName(product, 0);
        const optionTags = variants
          .map((variant) => {
            const variantImage = this.getVariantImage(variant);
            const imageAttr = variantImage ? ` data-image="${this.resizeImage(variantImage, 300)}"` : '';
            const priceAttr = variant.price != null ? ` data-price="${variant.price}"` : '';
            const compareAttr =
              variant.compare_at_price != null ? ` data-compare-at-price="${variant.compare_at_price}"` : '';
            return `<option value="${variant.id}"${imageAttr}${priceAttr}${compareAttr} ${
              variant.available === false ? 'disabled' : ''
            }>${this.escapeHtml(this.getVariantTitle(variant))}${
              variant.available === false ? ' - Sold out' : ''
            }</option>`;
          })
          .join('');
        formFields = `
          <div class="select crosssell-card__select">
            <select class="select__select" data-crosssell-variant-select aria-label="${this.escapeHtml(
              product.title
            )}">
              <option value="" disabled selected>${this.escapeHtml(optionName)}</option>
              ${optionTags}
            </select>
            <span class="svg-wrapper crosssell-card__caret"><svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg></span>
          </div>
          <button type="button" class="crosssell-card__submit button" data-crosssell-submit disabled>+ Add</button>
        `;
      }

      return `
        <div class="crosssell-card" role="listitem" data-crosssell-card data-product-id="${product.id}">
          <a href="${product.url}" class="crosssell-card__media" tabindex="-1" aria-hidden="true">
            ${imageUrl ? `<img class="crosssell-card__image" data-crosssell-image src="${imageUrl}" data-default-src="${imageUrl}" alt="${this.escapeHtml(
              image && image.alt ? image.alt : product.title
            )}" loading="lazy" width="300" height="300">` : ''}
          </a>
          <div class="crosssell-card__info">
            <div class="crosssell-card__heading">
              <a href="${product.url}" class="crosssell-card__title">${this.escapeHtml(product.title)}</a>
              ${badge}
            </div>
            <div class="crosssell-card__price">${price}</div>
            <div class="crosssell-card__form" data-crosssell-form data-product-id="${product.id}">
              ${formFields}
            </div>
          </div>
        </div>
      `;
    }

    resizeImage(src, width) {
      if (!src) return '';
      return src.replace(/(\.[a-zA-Z0-9]+)(\?|$)/, `_${width}x$1$2`);
    }

    getOptionName(product, index) {
      const option = product.options && product.options[index];
      if (!option) return 'Options';
      if (typeof option === 'string') return option;
      return option.name || 'Options';
    }

    getVariantTitle(variant) {
      if (!variant) return '';
      if (typeof variant.title === 'string' && variant.title) return variant.title;
      const parts = [variant.option1, variant.option2, variant.option3].filter(Boolean);
      return parts.length ? parts.join(' / ') : '';
    }

    getVariantImage(variant) {
      if (!variant) return '';
      const image = variant.featured_image || variant.image || null;
      if (!image) return '';
      return typeof image === 'string' ? image : image.src || '';
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value == null ? '' : String(value);
      return div.innerHTML;
    }

    formatMoney(cents) {
      if (cents == null) return '';
      const amount = (cents / 100).toFixed(2);
      const parts = amount.split('.');
      const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const formatted = `${withCommas}.${parts[1]}`;
      return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, formatted);
    }

    onVariantChange(event) {
      const select = event.target.closest('[data-crosssell-variant-select]');
      if (!select) return;
      event.stopPropagation();
      const form = select.closest('[data-crosssell-form]');
      const submit = form && form.querySelector('[data-crosssell-submit]');
      if (!submit) return;
      const option = select.options[select.selectedIndex];
      submit.disabled = !(select.value && option && !option.disabled);

      const card = select.closest('[data-crosssell-card]');
      const image = card && card.querySelector('[data-crosssell-image]');
      if (image) {
        const newSrc = (option && option.dataset.image) || image.dataset.defaultSrc;
        if (newSrc) image.src = newSrc;
      }

      const compareEl = card && card.querySelector('[data-crosssell-price-compare]');
      const finalEl = card && card.querySelector('[data-crosssell-price-final]');
      const price = option && option.dataset.price ? parseInt(option.dataset.price, 10) : null;
      const comparePrice =
        option && option.dataset.compareAtPrice ? parseInt(option.dataset.compareAtPrice, 10) : null;
      if (finalEl && price != null) {
        finalEl.textContent = this.formatMoney(price);
      }
      if (compareEl) {
        if (comparePrice != null && price != null && comparePrice > price) {
          compareEl.textContent = this.formatMoney(comparePrice);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
    }

    onAddClick(event) {
      const submit = event.target.closest('[data-crosssell-submit]');
      if (!submit) return;
      event.stopPropagation();
      const form = submit.closest('[data-crosssell-form]');
      if (!form) return;

      const hiddenInput = form.querySelector('[data-crosssell-variant-id]');
      const select = form.querySelector('[data-crosssell-variant-select]');
      const variantId = hiddenInput ? hiddenInput.value : select ? select.value : '';
      if (!variantId || submit.disabled) return;
      if (!window.routes || !window.routes.cart_add_url) return;

      const cartTarget = document.querySelector('cart-drawer');
      const body = { items: [{ id: variantId, quantity: 1 }] };

      if (cartTarget && typeof cartTarget.getSectionsToRender === 'function') {
        body.sections = cartTarget.getSectionsToRender().map((section) => section.id || section.section);
        body.sections_url = window.location.pathname;
        if (typeof cartTarget.setActiveElement === 'function') {
          cartTarget.setActiveElement(document.activeElement);
        }
      }

      if (submit) {
        submit.classList.add('loading');
        submit.disabled = true;
      }

      fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body),
      })
        .then((res) => res.json())
        .then((response) => {
          if (response.status) return;
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-drawer-crosssell',
              productVariantId: variantId,
              cartData: response,
            });
          }
          if (cartTarget && typeof cartTarget.renderContents === 'function') {
            cartTarget.renderContents(response);
          }
        })
        .catch(() => {})
        .finally(() => {
          if (submit) {
            submit.classList.remove('loading');
            submit.disabled = false;
          }
        });
    }

    initCarousel() {
      const cards = this.track ? Array.from(this.track.children) : [];
      if (!this.controls) return;

      if (cards.length < 2 || this.track.scrollWidth <= this.track.clientWidth + 4) {
        this.controls.hidden = true;
        return;
      }

      this.controls.hidden = false;
      this.cardCount = cards.length;
      if (this.totalLabel) this.totalLabel.textContent = this.cardCount;
      this.updateProgress();
    }

    scrollByCards(direction) {
      if (!this.track || !this.track.firstElementChild) return;
      const card = this.track.firstElementChild;
      const style = getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap || style.gap || '0');
      const distance = card.getBoundingClientRect().width + gap;
      this.track.scrollBy({ left: distance * direction, behavior: 'smooth' });
    }

    onScroll() {
      window.requestAnimationFrame(() => this.updateProgress());
    }

    updateProgress() {
      if (!this.track || !this.cardCount) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const ratio = maxScroll > 0 ? this.track.scrollLeft / maxScroll : 0;

      if (this.progressValue) {
        this.progressValue.style.width = `${Math.min(100, Math.max(0, ratio * 100))}%`;
      }

      const index = Math.round(ratio * (this.cardCount - 1));
      if (this.currentLabel) this.currentLabel.textContent = index + 1;

      if (this.prevButton) this.prevButton.disabled = this.track.scrollLeft <= 0;
      if (this.nextButton) this.nextButton.disabled = ratio >= 0.999;
    }
  }

  customElements.define('cart-drawer-crosssell', CartDrawerCrosssell);
}
